import {
    type CSSProperties,
    type ReactNode,
    useState,
    useRef
} from "react";
import {
    Animated
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type IPageContainerRef,
    NCoreUIKitTheme,
    getSlideStyle,
    PageContainer,
    usePresence,
    Header,
    Button,
    Switch,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type DocsStyleTokens,
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    PlusIcon
} from "../../anatomy/icons";

const NAME = "PageContainer";

const USAGE_CODE = `const [isScrolled, setIsScrolled] = useState(false);

<Header
    isScrolled={isScrolled}
    title="Messages"
/>

<PageContainer
    onScrollEdgeChange={setIsScrolled}
    isWorkWithContentMaxWidth={true}
    isScrollable={true}
>
    {content}
</PageContainer>`;

const ITEMS = [
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8
] as const;

const NEW_ITEM_KEYS = [
    "pageContainerPage.newItem1",
    "pageContainerPage.newItem2",
    "pageContainerPage.newItem3",
    "pageContainerPage.newItem4",
    "pageContainerPage.newItem5"
];

type OverlayItemType = {
    messageKey?: string;
    isAdded: boolean;
    id: number;
};

const INITIAL_OVERLAY_ITEMS: Array<OverlayItemType> = ITEMS.map((id) => ({
    isAdded: false,
    id
}));

type OverlayListItemProps = {
    item: OverlayItemType;
    text: string;
};

const OverlayListItem = ({
    item,
    text
}: OverlayListItemProps) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        isInitialAnimated: item.isAdded,
        enterIntent: "listInsert",
        isVisible: true
    });

    if(!isRendered) {
        return null;
    }

    return <Animated.View
        style={getSlideStyle({
            offset: spaces.spacingSm,
            isReduceMotionEnabled,
            progress
        })}
    >
        <Text
            variant="bodyMediumSize"
            color="mid"
        >
            {text}
        </Text>
    </Animated.View>;
};

const getAreaStyle = ({
    shapeConfigs
}: DocsStyleTokens) => ({
    height: shapeConfigs.chartHeight.small
});

const getColumnStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    flexDirection: "column",
    gap: spaces.spacingSm,
    display: "flex",
    width: "100%"
});

const getOverlayStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    bottom: spaces.spacingMd,
    right: spaces.spacingMd,
    position: "absolute"
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("pageContainerPage.layoutTitle"),
        id: "layout"
    },
    {
        title: t("pageContainerPage.scrollingTitle"),
        id: "scrolling"
    },
    {
        title: t("pageContainerPage.overlaysTitle"),
        id: "overlays"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const PageContainerPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        isScrolled,
        setIsScrolled
    ] = useState(false);

    const [
        isCustomPadding,
        setIsCustomPadding
    ] = useState(false);

    const [
        overlayItems,
        setOverlayItems
    ] = useState<Array<OverlayItemType>>(INITIAL_OVERLAY_ITEMS);

    const overlayScrollRef = useRef<IPageContainerRef>(null);

    const isScrollPendingRef = useRef(false);

    const handlePaddingToggle = () => setIsCustomPadding(!isCustomPadding);

    const handleAddItem = () => {
        isScrollPendingRef.current = true;

        setOverlayItems((current) => {
            const lastId = current[current.length - 1]?.id ?? 0;

            return [
                ...current,
                {
                    messageKey: NEW_ITEM_KEYS[(current.length - ITEMS.length) % NEW_ITEM_KEYS.length],
                    id: lastId + 1,
                    isAdded: true
                }
            ];
        });
    };

    const handleOverlayContentSizeChange = () => {
        const node = overlayScrollRef.current;

        if(!isScrollPendingRef.current || !node || !("scrollToEnd" in node)) {
            return;
        }

        isScrollPendingRef.current = false;

        node.scrollToEnd({
            animated: true
        });
    };

    const renderOverlayItems = () => overlayItems.map((item) => <OverlayListItem
        text={item.messageKey ? t(item.messageKey) : t("pageContainerPage.item", [
            item.id
        ])}
        key={item.id}
        item={item}
    />);

    const renderItems = () => ITEMS.map((item) => <Text
        variant="bodyMediumSize"
        color="mid"
        key={item}
    >
        {t("pageContainerPage.item", [
            item
        ])}
    </Text>);

    const areaStyle = getAreaStyle(docsTokens);

    const renderFrame = (label: string, content: ReactNode) => <div
        style={getColumnStyle(docsTokens)}
        key={label}
    >
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
        <DemoFrame>
            {content}
        </DemoFrame>
    </div>;

    const renderOverlays = () => <div
        style={getOverlayStyle(docsTokens)}
    >
        <Button
            title={t("pageContainerPage.add")}
            onPress={handleAddItem}
            spreadBehaviour="free"
            icon={PlusIcon}
        />
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("pageContainerPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("pageContainerPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("pageContainerPage.overviewCaption")}
                isStacked={true}
            >
                <DemoFrame>
                    <Header
                        title={t("pageContainerPage.screenTitle")}
                        isWrapSafeAreaContext={false}
                        isWorkWithHeaderSpace={false}
                        isScrolled={isScrolled}
                    />
                    <PageContainer
                        onScrollEdgeChange={setIsScrolled}
                        isWorkWithHeaderSpace={false}
                        scrollViewStyle={areaStyle}
                        isScrollable={true}
                    >
                        {renderItems()}
                    </PageContainer>
                </DemoFrame>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("pageContainerPage.layoutTitle")}
            id="layout"
        >
            <Paragraph>
                {t("pageContainerPage.layoutText")}
            </Paragraph>
            <Preview
                caption={t("pageContainerPage.layoutCaption")}
                isStacked={true}
            >
                <DemoFrame>
                    <PageContainer
                        isCustomPadding={isCustomPadding}
                        isWorkWithHeaderSpace={false}
                        backgroundColor="mid"
                        style={areaStyle}
                    >
                        <Card
                            spreadBehaviour="stretch"
                            variant="filled"
                        >
                            <Card.Content>
                                <Text
                                    variant="bodyMediumSize"
                                    color="mid"
                                >
                                    {t("pageContainerPage.layoutBox")}
                                </Text>
                            </Card.Content>
                        </Card>
                    </PageContainer>
                </DemoFrame>
                <Switch
                    title={t("pageContainerPage.paddingSwitch")}
                    onPress={handlePaddingToggle}
                    isChecked={isCustomPadding}
                />
            </Preview>
        </Section>
        <Section
            title={t("pageContainerPage.scrollingTitle")}
            id="scrolling"
        >
            <Paragraph>
                {t("pageContainerPage.scrollingText")}
            </Paragraph>
            <Preview
                caption={t("pageContainerPage.scrollingCaption")}
                isStacked={true}
            >
                {
                    renderFrame("isScrollable={false}", <PageContainer
                        isWorkWithHeaderSpace={false}
                        style={areaStyle}
                    >
                        {renderItems()}
                    </PageContainer>)
                }
                {
                    renderFrame("isScrollable={true}", <PageContainer
                        isWorkWithHeaderSpace={false}
                        scrollViewStyle={areaStyle}
                        isScrollable={true}
                    >
                        {renderItems()}
                    </PageContainer>)
                }
            </Preview>
        </Section>
        <Section
            title={t("pageContainerPage.overlaysTitle")}
            id="overlays"
        >
            <Paragraph>
                {t("pageContainerPage.overlaysText")}
            </Paragraph>
            <Preview
                caption={t("pageContainerPage.overlaysCaption")}
                isStacked={true}
            >
                <DemoFrame>
                    <PageContainer
                        scrollViewProps={{
                            onContentSizeChange: handleOverlayContentSizeChange
                        }}
                        renderOverlays={renderOverlays}
                        isWorkWithHeaderSpace={false}
                        scrollViewStyle={areaStyle}
                        ref={overlayScrollRef}
                        isScrollable={true}
                    >
                        {renderOverlayItems()}
                    </PageContainer>
                </DemoFrame>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("pageContainerPage.a11yRole"),
                    t("pageContainerPage.a11yScroll"),
                    t("pageContainerPage.a11ySafe"),
                    t("pageContainerPage.a11yOverlay"),
                    t("pageContainerPage.a11yWarn")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("pageContainerPage.propIsScrollable"),
                        defaultValue: "false",
                        name: "isScrollable",
                        type: "boolean"
                    },
                    {
                        description: t("pageContainerPage.propScrollViewProps"),
                        name: "scrollViewProps",
                        type: "ScrollViewProps"
                    },
                    {
                        description: t("pageContainerPage.propScrollViewStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "scrollViewStyle"
                    },
                    {
                        description: t("pageContainerPage.propOnScrollEdgeChange"),
                        type: "(isScrolled: boolean) => void",
                        name: "onScrollEdgeChange"
                    },
                    {
                        description: t("pageContainerPage.propBackgroundColor"),
                        type: "keyof ContainerContentColors",
                        defaultValue: "\"default\"",
                        name: "backgroundColor"
                    },
                    {
                        description: t("pageContainerPage.propIsCustomPadding"),
                        name: "isCustomPadding",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("pageContainerPage.propIsWorkWithContentMaxWidth"),
                        name: "isWorkWithContentMaxWidth",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("pageContainerPage.propIsWorkWithHeaderSpace"),
                        name: "isWorkWithHeaderSpace",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("pageContainerPage.propIsWrapSafeAreaContext"),
                        name: "isWrapSafeAreaContext",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("pageContainerPage.propSafeAreaViewBackgroundColor"),
                        name: "safeAreaViewBackgroundColor",
                        type: "keyof ContainerContentColors"
                    },
                    {
                        description: t("pageContainerPage.propSafeAreaViewStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "safeAreaViewStyle"
                    },
                    {
                        description: t("pageContainerPage.propRenderOverlays"),
                        type: "() => ReactNode",
                        name: "renderOverlays"
                    },
                    {
                        description: t("pageContainerPage.propStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "style"
                    },
                    {
                        description: t("pageContainerPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("pageContainerPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("pageContainerPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default PageContainerPage;
